import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getGroups, saveGroup } from "../../components/Api/GmsGroup";
import { SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  group_Id: 0,
  group_Name: "",
  group_NameAR: "",
  description: "",
  status: true,
  created_By: 1,
  created_Date: new Date().toISOString(),
  modified_By: 1,
  modified_Date: new Date().toISOString(),
};

const emptyFilter = {
  group_Name: "",
  status: null,
};

export default function GroupPage() {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsGroup');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsGroup');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsGroup');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsGroup');
  const isAdmin = isUserAdmin();

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadGroups(); // Load groups on component mount
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadGroups();
    }
  }, [hasAppliedFilter]);

  const loadGroups = async (filterData = filter) => {
    setLoading(true);
    try {
      const data = await getGroups(filterData);
      setGroups(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load groups: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // If status is changed, update the filter immediately
    if (field === 'status') {
      setHasAppliedFilter(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.group_Name) {
      Swal.fire("Error", "Please fill in Group Name", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();
      const groupData = {
        ...form,
        created_By: isEditing ? form.created_By : session.userId,
        modified_By: session.userId,
        modified_Date: new Date().toISOString(),
      };

      const result = await saveGroup(groupData);
      
      if (result.group_Id) {
        Swal.fire("Success", "Group saved successfully!", "success");
        handleClear();
        if (hasAppliedFilter) {
          loadGroups();
        }
      }
    } catch (error) {
      let errorMessage = "Failed to save group";
      
      // Extract error message from API response
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          // Extract just the error message from the exception string
          const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
          errorMessage = match ? match[1].trim() : error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (group) => {
    setEditLoadingRow(group.group_Id);
    try {
      setForm({
        group_Id: group.group_Id,
        group_Name: group.group_Name || "",
        group_NameAR: group.group_NameAR || "",
        description: group.description || "",
        status: group.status,
        created_Date: group.created_Date,
        created_By: group.created_By,
        modified_Date: group.modified_Date,
        modified_By: group.modified_By,
      });
      setIsEditing(true);
      setActiveTab("1"); // Switch to form tab
    } catch (error) {
      Swal.fire("Error", "Failed to load group data", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleClear = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadGroups(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(false);
    setGroups([]);
    setTotalCount(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getFilteredGroups = () => {
    if (!hasAppliedFilter) return groups;
    
    return groups.filter(group => {
      const nameMatch = !filter.group_Name || 
        group.group_Name.toLowerCase().includes(filter.group_Name.toLowerCase());
      const statusMatch = filter.status === null || group.status === filter.status;
      return nameMatch && statusMatch;
    });
  };

  const getPaginatedGroups = () => {
    const filteredGroups = getFilteredGroups();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredGroups.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      // Refresh groups when switching to Group List tab
      if (tab === "2") {
        loadGroups();
      }
    }
  };

  const getGroupNameOptions = () => {
    return groups.map(group => ({
      value: group.group_Name,
      label: group.group_Name
    }));
  };

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Group Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {!isViewOnly && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Group Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Group List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  {!isViewOnly && (
                    <TabPane tabId="1">
                      <div>
                        <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="group_Name">Group Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                type="text"
                                id="group_Name"
                                value={form.group_Name}
                                onChange={(e) => handleFormChange("group_Name", e.target.value)}
                                placeholder="Enter group name in English"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="group_NameAR">Group Name (Arabic)</Label>
                              <Input
                                type="text"
                                id="group_NameAR"
                                value={form.group_NameAR}
                                onChange={(e) => handleFormChange("group_NameAR", e.target.value)}
                                placeholder="Enter group name in Arabic"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={8}>
                            <FormGroup className="mb-4">
                              <Label for="description">Description</Label>
                              <Input
                                type="textarea"
                                id="description"
                                rows="3"
                                value={form.description}
                                onChange={(e) => handleFormChange("description", e.target.value)}
                                placeholder="Enter group description"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup className="mb-4">
                              <div className="form-check form-switch">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="group_status"
                                  name="status"
                                  checked={form.status}
                                  onChange={(e) => handleFormChange("status", e.target.checked)}
                                  disabled={!hasDeletePermission}
                                />
                                <label className="form-check-label" htmlFor="group_status">
                                  {form.status ? "Active" : "Inactive"}
                                </label>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Group" : "Save Group"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                        </Form>
                      </div>
                    </TabPane>
                  )}
                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Groups</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={4}>
                                <Label for="filterGroupName">Group Name</Label>
                                <Select
                                  id="filterGroupName"
                                  value={getGroupNameOptions().find(option => option.value === filter.group_Name)}
                                  onChange={(selectedOption) => 
                                    handleFilterChange("group_Name", selectedOption ? selectedOption.value : "")
                                  }
                                  options={getGroupNameOptions()}
                                  isClearable
                                  isSearchable
                                  placeholder="Search group name..."
                                  className="react-select"
                                  classNamePrefix="react-select"
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterStatus">Status</Label>
                                <Select
                                  id="filterStatus"
                                  value={statusOptions.find(option => option.value === filter.status)}
                                  onChange={(selectedOption) =>
                                    handleFilterChange("status", selectedOption ? selectedOption.value : null)
                                  }
                                  options={statusOptions}
                                  isClearable
                                  placeholder="Select Status"
                                  className="react-select"
                                  classNamePrefix="react-select"
                                />
                              </Col>
                              <Col md={4} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Group List
                            </h5>
                            {!isViewOnly && (
                              <Button
                                color="primary"
                                onClick={() => toggle("1")}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Group
                              </Button>
                            )}
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading groups...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="20%">Group Name (En)</th>
                                      <th width="20%">Group Name (Ar)</th>
                                      <th width="25%">Description</th>
                                      <th width="10%">Status</th>
                                      <th width="15%">Created Date</th>
                                      <th width="5%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getPaginatedGroups().length > 0 ? (
                                      getPaginatedGroups().map((group, index) => (
                                        <tr key={group.group_Id}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{group.group_Name}</td>
                                          <td>{group.group_NameAR || "N/A"}</td>
                                          <td>{group.description || "N/A"}</td>
                                          <td>
                                            <span className={`badge ${group.status ? 'bg-success' : 'bg-danger'}`}>
                                              {group.status ? "Active" : "Inactive"}
                                            </span>
                                          </td>
                                          <td>{formatDateTime(group.created_Date)}</td>
                                          <td>
                                            {!isViewOnly && (
                                              <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handleEdit(group)}
                                                disabled={editLoadingRow === group.group_Id}
                                                className="waves-effect waves-light"
                                              >
                                                {editLoadingRow === group.group_Id ? (
                                                  <ClipLoader size={12} color="#ffffff" />
                                                ) : (
                                                  <SquarePen size={12} />
                                                )}
                                              </Button>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="7" className="text-center">
                                          {hasAppliedFilter ? "No groups found matching the filter criteria." : "No groups available. Please add some groups first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {getTotalPages() > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {getTotalPages()}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === getTotalPages()}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
